import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ExternalLink, FileText, ImageOff, X } from 'lucide-react';
import { Popover, Tooltip } from '../ui';

/* ---------------------------------------------------------------------------
 * Attachments — thumbnail, "+N" badge, and an in-page viewer.
 *
 * Proof files used to be a link icon that threw the user into a new browser tab: they lost the
 * page, saw one file, and had no way to reach the second one without coming back. Here a file is
 * shown as what it is (a thumbnail), extra files announce themselves with a +N badge, and opening
 * one keeps the user on the page — arrow keys walk the set.
 * ------------------------------------------------------------------------- */

/** Same test DocumentPreview/FileUploadField use, so a file looks the same wherever it is shown. */
const isImage = (u: string) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);
const isPdf = (u: string) => /\.pdf(\?|$)/i.test(u);

/** ".../1722001700000-ab12cd34.jpg" -> "JPG". Stored names are machine-generated, so the extension
 *  is the only honest thing in them — the caller's label carries the meaning instead. */
function extOf(u: string) {
  const m = /\.([a-z0-9]{1,5})(\?|$)/i.exec(u);
  return m ? m[1].toUpperCase() : 'FILE';
}

/**
 * A file's tile. An image shows itself; everything else NAMES ITSELF.
 *
 * A proof can be a photo of a slip or a PDF receipt, and both must read as "a document is here, of
 * this kind". A single anonymous grey glyph for every non-image said none of that: a PDF receipt, a
 * file type nobody can preview, and an image whose URL is dead all looked identical, so a reader
 * could not tell a real attachment from a broken one without clicking each.
 *
 *  · PDF        — its own tone and the word PDF; clicking previews it inline (see AttachmentViewer)
 *  · other type — its extension, so the reader knows what they are about to open
 *  · dead image — flagged as such, because a link that no longer resolves is a data problem, not a
 *                 document. (Dev data is full of these: fixture URLs whose files were never written
 *                 come back as the SPA's index.html and fail to decode.)
 */
function Thumb({ url, alt, className = '', size = 32 }: { url: string; alt: string; className?: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (isImage(url) && !broken) {
    return (
      <img
        src={url}
        alt={alt}
        loading="lazy"
        onError={() => setBroken(true)}
        className={`size-full rounded-control border border-line object-cover ${className}`}
      />
    );
  }
  const pdf = isPdf(url);
  const deadImage = broken;
  /* A tile is ~32px. Anything written on it has to FIT there: an extension does (PDF, DOCX), a
     sentence does not — "MISSING" ran straight out of its box. So a broken link is a GLYPH, and the
     explanation lives in the tooltip where there is room for it. */
  const ext = extOf(url).slice(0, 4);
  // Below ~40px there is no room for a glyph AND a word; the word is the one that carries meaning.
  const roomy = size >= 40;
  const tone = pdf
    ? 'border-danger-200 bg-danger-50 text-danger-700 dark:border-danger-500/40 dark:bg-danger-500/10 dark:text-danger-400'
    : deadImage
      ? 'border-warning-200 bg-warning-50 text-warning-700 dark:border-harvest-500/40 dark:bg-harvest-500/10 dark:text-harvest-300'
      : 'border-line bg-surface-2 text-subtle';
  return (
    <span
      title={deadImage ? `${alt} — this ${ext} file could not be loaded` : `${alt} · ${ext}`}
      className={`grid size-full place-items-center gap-0.5 overflow-hidden rounded-control border text-center leading-none ${tone} ${className}`}
    >
      {deadImage ? (
        <ImageOff size={roomy ? 16 : 13} aria-hidden />
      ) : (
        <>
          {roomy && <FileText size={14} aria-hidden />}
          {/* ui-ok: an extension glyph inside a 13–16px tile; the scale floor (11px) would not fit. */}
          <span className="max-w-full px-0.5 text-[8px] font-semibold tracking-wide">{ext}</span>
        </>
      )}
    </span>
  );
}

/**
 * Fullscreen viewer for a set of files. Left/Right walk the set, Escape closes, and a PDF renders
 * inline in a frame rather than being handed off to the browser's downloader.
 */
export function AttachmentViewer({
  files, index, label, labels, onIndex, onClose,
}: { files: string[]; index: number; label: string; labels?: string[]; onIndex: (i: number) => void; onClose: () => void }) {
  const at = Math.min(Math.max(index, 0), files.length - 1);
  const url = files[at];
  /* A set can be several DIFFERENT documents — a payment receipt and the bill the advance was asked
     against are not "Proof 1" and "Proof 2". When the caller names them, the name is what the
     reader is shown; otherwise the set falls back to "<label> 2 of 3". */
  const nameAt = (i: number) => labels?.[i] || `${label} ${i + 1}`;

  const go = useCallback((delta: number) => {
    onIndex(Math.min(Math.max(at + delta, 0), files.length - 1));
  }, [at, files.length, onIndex]);

  useEffect(() => {
    /*
     * WINDOW, CAPTURE PHASE — and the keys this layer handles stop there.
     *
     * This overlay is often opened from inside a Radix dialog, and Radix listens for Escape on the
     * DOCUMENT in the capture phase, registered when that dialog opened — i.e. before this one. One
     * Escape therefore closed the preview AND the record behind it, so a reader who glanced at a
     * photo lost the receipt they were reading and had to find the row again. Capture on `window`
     * runs one step earlier in the path than capture on `document`, so stopping propagation here
     * lets the topmost layer take the key, which is what "topmost" is supposed to mean.
     */
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); go(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); go(1); }
    };
    window.addEventListener('keydown', onKey, true);
    // Lock background scroll while the overlay is up, restoring whatever was there before.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  }, [go, onClose]);

  if (!url) return null;

  const caption = labels?.[at] || (files.length > 1 ? `${label} ${at + 1} of ${files.length}` : label);
  const navBtn = 'grid size-11 shrink-0 self-center place-items-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-30';

  return createPortal(
    <div
      /*
       * `pointer-events-auto` is load-bearing, not decoration.
       *
       * This overlay portals to <body>, and a Radix modal dialog sets `pointer-events: none` on the
       * body while it is open so that only the dialog itself is interactive. Opened from inside one
       * — a product photo in the GRN receipt, say — the preview would render on top, correctly, and
       * then ignore every click: no close, no arrows, no "Open original". Esc still worked, because
       * that is a document key listener, which makes the failure look like a rendering bug rather
       * than a dead layer. Re-enabling pointer events for this subtree is the whole fix.
       */
      className="pointer-events-auto fixed inset-0 z-[var(--z-lightbox)] flex flex-col bg-black/85 p-4 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={caption}
      onClick={onClose}
    >
      <div className="flex shrink-0 items-center justify-between gap-3 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="truncate type-body-strong">{caption}</span>
        <span className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-9 items-center gap-1.5 rounded-control bg-white/15 px-3 type-body text-white transition-colors hover:bg-white/25"
          >
            <ExternalLink size={16} />Open original
          </a>
          <button type="button" aria-label="Close preview" onClick={onClose} className="grid size-9 place-items-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25">
            <X size={18} />
          </button>
        </span>
      </div>

      {/*
        The stage STRETCHES its children; it does not centre them.
        `max-h-full` on the picture resolves against its parent's height, and under `items-center`
        that parent's height is auto — i.e. the picture's own — so the constraint meant nothing and a
        1150px photo in a 1000px window simply grew past the top of the screen, covering the header
        and swallowing clicks meant for the close button. Stretching gives the wrapper a real height
        to measure against; the centring moves down one level, where it costs nothing.
      */}
      <div className="flex min-h-0 flex-1 items-stretch justify-center gap-3 py-4" onClick={onClose}>
        {files.length > 1 && (
          <button type="button" aria-label="Previous attachment" disabled={at === 0} className={navBtn}
            onClick={(e) => { e.stopPropagation(); go(-1); }}><ChevronLeft size={22} /></button>
        )}
        <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {isImage(url) ? (
            <img src={url} alt={caption} className="max-h-full max-w-full rounded-lg object-contain shadow-pop" />
          ) : isPdf(url) ? (
            // Uploads are same-origin (the API's /v1/files is proxied), so helmet's SAMEORIGIN frame
            // guard is satisfied and the PDF renders here instead of in a new tab.
            // ui-ok: a PDF page is white paper in either theme — a surface-token backdrop would show through its margins.
            <iframe src={url} title={caption} className="h-full w-full max-w-4xl rounded-lg border-0 bg-white" />
          ) : (
            <div className="grid place-items-center gap-3 rounded-card bg-surface p-8 text-center">
              <FileText size={32} className="text-subtle" />
              <p className="type-body text-muted">A {extOf(url)} file cannot be previewed here.</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="type-body-strong text-brand-700 hover:underline dark:text-brand-400">Open it in a new tab</a>
            </div>
          )}
        </div>
        {files.length > 1 && (
          <button type="button" aria-label="Next attachment" disabled={at === files.length - 1} className={navBtn}
            onClick={(e) => { e.stopPropagation(); go(1); }}><ChevronRight size={22} /></button>
        )}
      </div>

      {files.length > 1 && (
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
          {files.map((f, i) => (
            <button
              key={f + i}
              type="button"
              aria-label={nameAt(i)}
              aria-current={i === at}
              onClick={() => onIndex(i)}
              className={`size-12 overflow-hidden rounded-control ring-2 transition-all ${i === at ? 'ring-white' : 'opacity-60 ring-transparent hover:opacity-100'}`}
            >
              <Thumb url={f} alt={nameAt(i)} />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}

/**
 * Table-cell renderer for a set of attachments: thumbnails, a "+N" badge for whatever does not fit,
 * and a click that previews IN PAGE.
 *
 * Up to `maxThumbs` files are shown outright — with two proofs the reader should see both, not a
 * count. Past that the overflow badge appears and a click opens the full list first, because
 * jumping straight into file 1 would hide the very files the badge is announcing.
 */
export function AttachmentCell({
  files, label, labels, empty = <span className="text-subtle">&mdash;</span>, size = 32, maxThumbs = 2,
}: {
  /* Holes allowed: a caller listing "payment proof, request document" for a row that has only one of
     them passes the gap through rather than compacting the array, which is what keeps `labels`
     lined up with the files that survive. */
  files: (string | null | undefined)[]; label: string; labels?: string[];
  empty?: ReactNode; size?: number; maxThumbs?: number;
}) {
  const [open, setOpen] = useState(false);
  const [viewAt, setViewAt] = useState<number | null>(null);
  /*
   * `files` and `labels` are indexed TOGETHER, and a caller that drops a missing file — `[a, b]
   * .filter(Boolean)` — would slide every name onto the wrong picture. So the pair is filtered as
   * one: a blank url takes its name out of the list with it.
   */
  const kept = (files ?? [])
    .map((f, i) => [f, labels?.[i]] as const)
    .filter((e): e is readonly [string, string | undefined] => !!e[0]);
  const list = kept.map(([f]) => f);
  const nameAt = (i: number) => kept[i]?.[1] || (list.length > 1 ? `${label} ${i + 1}` : label);
  if (!list.length) return <>{empty}</>;

  const shown = list.slice(0, maxThumbs);
  const overflow = list.length - shown.length;
  const view = (i: number) => { setOpen(false); setViewAt(i); };

  const thumbBtn = (url: string, i: number, onClick: () => void) => (
    <button
      key={url + i}
      type="button"
      aria-label={nameAt(i)}
      onClick={onClick}
      className="inline-block shrink-0 rounded-control transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
      style={{ width: size, height: size }}
    >
      <Thumb url={url} alt={nameAt(i)} size={size} />
    </button>
  );

  // The badge sits on the GROUP, so it reads as "and N more" rather than as a count on one file.
  const group = (
    <span className="relative inline-flex items-center gap-1">
      {shown.map((f, i) => thumbBtn(f, i, () => (overflow > 0 ? setOpen((o) => !o) : view(i))))}
      {overflow > 0 && (
        // ui-ok: a "+N" count inside a 16px circle; the scale floor (11px) would burst the badge.
        <span className="pointer-events-none absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-none text-white shadow-sm">
          +{overflow}
        </span>
      )}
    </span>
  );

  return (
    <>
      {overflow > 0 ? (
        <Popover
          open={open}
          onOpenChange={setOpen}
          placement="bottomRight"
          title={`${list.length} files`}
          overlayClassName="max-w-none"
          content={
            /*
             * A grid of the pictures themselves, not a list of what they are called.
             *
             * The names here are generated ("Visit V-MTI6LSWR photo 2"), so a column of them told the
             * reader nothing they could not already see and pushed the actual evidence down to a 36px
             * stamp. Showing the images at a size you can recognise is the whole reason this popover
             * exists; the number stays only as a corner badge, to keep the order addressable.
             */
            <div className="grid max-w-[19rem] grid-cols-3 gap-1.5">
              {list.map((f, i) => (
                <button
                  key={f + i}
                  type="button"
                  aria-label={nameAt(i)}
                  onClick={() => view(i)}
                  className="relative aspect-square overflow-hidden rounded-control transition-transform hover:scale-[1.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                >
                  <Thumb url={f} alt={nameAt(i)} size={92} />
                  {/* ui-ok: an index inside a 16px circle; the scale floor (11px) would burst the badge. */}
                  <span className="pointer-events-none absolute bottom-0.5 right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-black/65 px-1 text-[10px] font-semibold leading-none text-white">
                    {i + 1}
                  </span>
                </button>
              ))}
            </div>
          }
        >
          {group}
        </Popover>
      ) : (
        <Tooltip label={list.length > 1 ? `${list.map((_, i) => nameAt(i)).join(' · ')} — click to preview` : nameAt(0)}>{group}</Tooltip>
      )}
      {viewAt !== null && (
        <AttachmentViewer files={list} index={viewAt} label={label} labels={kept.map(([, n]) => n ?? '')} onIndex={setViewAt} onClose={() => setViewAt(null)} />
      )}
    </>
  );
}
